import useCreateResetPasswordRequest from "api/mutations/useCreateResetPasswordRequest";
import useSubmitResetPasswordRequest from "api/mutations/useSubmitResetPasswordRequest";
import useGetResetPasswordRequest from "api/queries/useGetResetPasswordRequest";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isValidPassword } from "utils/usePasswordInputState";
import useSnackbar from "utils/useSnackbar";

interface RequestResetPasswordFormValues {
	email: string;
}

interface ResetPasswordFormValues {
	password: string;
}

const useController = () => {
	/** common controller logic */
	const { handleError } = useSnackbar();
	const navigate = useNavigate();

	/** request reset password controller logic */
	const { mutateAsync: createResetPasswordRequest } =
		useCreateResetPasswordRequest();

	const handleCreateResetPasswordRequest = (data: FieldValues) => {
		const { email } = data as RequestResetPasswordFormValues;

		createResetPasswordRequest({ email })
			.then((variables) => {
				if (variables.error) {
					handleError(variables.error);
				} else {
					navigate("/signin", {
						state: {
							successMessage: `An email has been sent to ${email} with a password reset link. Please check your email within 3 days or the link will expire.`,
						},
					});
				}
			})
			.catch((error) => handleError(error));
	};

	/** reset password controller logic */

	const [searchParams] = useSearchParams();
	const code = searchParams.get("code") ?? "";

	const { mutateAsync: submitResetPasswordRequest } =
		useSubmitResetPasswordRequest();
	const {
		data: resetPasswordRequest,
		isInitialLoading: isInititalLoadingResetPasswordRequest,
	} = useGetResetPasswordRequest({ code });

	const passwordPolicy = resetPasswordRequest?.account_password_policy;
	const [password, setPassword] = useState("");
	const passwordValidationDetails =
		password ? isValidPassword(password, passwordPolicy) : null;

	const validateNewPassword = (value: string) => {
		const details = isValidPassword(value, passwordPolicy);
		return (
			!details ||
			Object.values(details).every(Boolean) ||
			"Please enter a valid password."
		);
	};

	const [submitError, setSubmitError] = useState("");

	const handleSubmitResetPasswordRequest = (data: FieldValues) => {
		const { password: newPassword } = data as ResetPasswordFormValues;
		if (!code) return;

		const params = { code, password: newPassword };

		submitResetPasswordRequest(params)
			.then((variables) => {
				if (variables.error) {
					setSubmitError(variables.error.message);
				} else if (variables.success) {
					navigate("/signin", {
						state: {
							successMessage:
								"Your password has been changed. Sign in with your new password.",
						},
					});
				}
			})
			.catch((error) => handleError(error));
	};

	return {
		code,
		handleCreateResetPasswordRequest,
		handleSubmitResetPasswordRequest,
		isInititalLoadingResetPasswordRequest,
		navigate,
		password,
		passwordValidationDetails,
		resetPasswordRequest,
		setPassword,
		submitError,
		validateNewPassword,
	};
};

export default useController;
